import { Button } from "@/components/core/buttons/button";
import { JournalAccountSelect } from "@/components/invoices/journal-account-select";
import {
	collectionItemsQueryOptions,
	usePostCollectionReceiptMutation,
	useUpdateInvoiceJournalOverridesMutation,
} from "@/features/dashboard/api/queries/journal-accounts-query";
import type { JournalAccountPurpose } from "@/features/dashboard/types/chart-of-accounts";
import { useQuery } from "@tanstack/react-query";

const phpFormat = (n: number) => n.toLocaleString("en-PH", { style: "currency", currency: "PHP" });

interface CollectionReceiptTabProps {
	requestId: string;
}

export function CollectionReceiptTab({ requestId }: CollectionReceiptTabProps) {
	const { data, isLoading, isError } = useQuery(collectionItemsQueryOptions(requestId));
	const postMutation = usePostCollectionReceiptMutation(requestId);
	const invoiceId = data?.invoice_id ?? "";
	const overrideMutation = useUpdateInvoiceJournalOverridesMutation(invoiceId);

	if (isLoading) {
		return <div className="py-8 text-center text-sm text-gray-500">Loading collection receipt…</div>;
	}

	if (isError || !data) {
		return <div className="py-8 text-center text-sm text-gray-500">Unable to load collection receipt.</div>;
	}

	const hasInvoice = data.has_invoice !== false && !!data.invoice_id;
	const isPosted = data.status === "posted" && !!data.entry_number;
	const isBooksDraft = data.status === "draft" && !!data.entry_number;
	const isDraft = !isPosted;
	const canEditAccounts = isDraft && hasInvoice;

	if (!hasInvoice) {
		return (
			<div className="rounded border border-amber-200 bg-amber-50 px-3 py-4 text-sm text-amber-800">
				The Collection Receipt JE can only be generated after this request is issued as an invoice. Process the request first.
			</div>
		);
	}

	if (data.lines.length === 0) {
		return (
			<div className="py-8 text-center text-sm text-gray-500">No collection JE yet — record a payment (with deductions if any) on this request first.</div>
		);
	}

	return (
		<div className="flex flex-col gap-3">
			<div className="flex items-start justify-between gap-4 text-xs text-gray-500">
				<div className="space-y-1">
					<div>
						JE number: <span className="font-mono text-gray-900">{data.entry_number ?? "— (draft)"}</span>
					</div>
					<div>
						Entry date: <span className="text-gray-900">{data.entry_date ?? "—"}</span>
					</div>
					{data.posted_at && (
						<div>
							Posted at: <span className="text-gray-900">{new Date(data.posted_at).toLocaleString("en-PH")}</span>
						</div>
					)}
				</div>
				<div className="flex flex-col items-end gap-2">
					<div className="flex items-center gap-2">
						<StatusBadge posted={isPosted} booksDraft={isBooksDraft} />
						<span className={data.is_balanced ? "text-green-700" : "font-semibold text-red-600"}>{data.is_balanced ? "Balanced" : "Unbalanced"}</span>
					</div>
					{isDraft && (
						<Button
							size="sm"
							onClick={() => postMutation.mutate("draft")}
							disabled={postMutation.isPending || !data.is_balanced}
							title={
								isBooksDraft
									? "Replace the draft in Books with the current local edits."
									: "Push the Collection JE to Books as a draft. Posting to the GL happens inside Books."
							}
						>
							{postMutation.isPending ? "Sending…" : isBooksDraft ? "Re-send to Books" : "Send to Books"}
						</Button>
					)}
					{isBooksDraft && (
						<span className="text-xs text-blue-700">
							Already in Books as <span className="font-mono">{data.entry_number}</span>. Post it from Books.
						</span>
					)}
				</div>
			</div>

			{data.last_error && (
				<div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">Last post attempt failed: {data.last_error}</div>
			)}

			<div className="overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-gray-200">
							<th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase w-[40%]">Account</th>
							<th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase">Label</th>
							<th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase">Debit</th>
							<th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase">Credit</th>
						</tr>
					</thead>
					<tbody>
						{data.lines.map((line) => (
							<tr key={`${line.purpose ?? line.account_code}-${line.debit}-${line.credit}`} className="border-b border-gray-100">
								<td className="py-2 px-3">
									{canEditAccounts && line.purpose ? (
										<JournalAccountSelect
											value={line.account_code}
											disabled={overrideMutation.isPending}
											onChange={(code, _name) => overrideMutation.mutate({ [line.purpose as JournalAccountPurpose]: code === line.account_code ? null : code })}
										/>
									) : (
										<span className="whitespace-nowrap">
											<span className="font-mono text-xs text-gray-500">{line.account_code}</span> <span className="text-gray-900">{line.account_name}</span>
										</span>
									)}
								</td>
								<td className="py-2 px-3 text-gray-700">{line.description}</td>
								<td className="py-2 px-3 text-right tabular-nums">{line.debit > 0 ? phpFormat(line.debit) : "—"}</td>
								<td className="py-2 px-3 text-right tabular-nums">{line.credit > 0 ? phpFormat(line.credit) : "—"}</td>
							</tr>
						))}
					</tbody>
					<tfoot className="border-t-2 border-gray-300 font-semibold">
						<tr>
							<td colSpan={2} className="py-2 px-3 text-right text-xs uppercase tracking-wide text-gray-500">
								Totals
							</td>
							<td className="py-2 px-3 text-right tabular-nums">{phpFormat(data.total_debit)}</td>
							<td className="py-2 px-3 text-right tabular-nums">{phpFormat(data.total_credit)}</td>
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	);
}

function StatusBadge({ posted, booksDraft }: { posted: boolean; booksDraft?: boolean }) {
	if (posted) {
		return <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-green-800">Posted</span>;
	}
	if (booksDraft) {
		return <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-blue-800">Draft in Books</span>;
	}
	return <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-amber-800">Draft</span>;
}
